// @givmo/board — the governing-document registry: reading a version CHAIN.
//
// ══ FOUR MODEL FACTS, AND EACH ONE IS A WAY THIS SCREEN COULD LIE ═══════════════════════════════
//
//   * A GOVERNING DOCUMENT IS A CHAIN OF RECORDS, NOT A ROW. Every version the organization adopted is its
//     own `board_record` of kind `governing_document`, and the chain is what makes "the version in
//     force" a single answer. There is no edit path and no second store: a new version is authored and
//     approved through the record core's own routes, which is why this feature's write flows call
//     `POST …/records` and `POST …/imported-records` rather than anything named after a document.
//
//   * VERSION LINEAGE (`supersedes_record_id`) IS NOT CORRECTION LINEAGE (`corrects_record_id`), and a
//     record carries one or the other — a CHECK refuses both on one row. A new version says the board
//     adopted a replacement; a correction says the earlier record was WRONG. Rendering them with one
//     word would tell an organization its board replaced an instrument when in fact somebody fixed a typo.
//     Corrections are excluded from the chain read entirely (a correction keeps its version's place),
//     so the only lineage this module renders is the version kind — {@link versionLineage} says so in
//     its own words, and `features/records/` renders the other kind under its own heading.
//
//   * THE ORDER IS WALKED FROM THE POINTERS, NEVER SORTED BY DATE. Two versions adopted on one day
//     still have an unambiguous order, and a back-dated version cannot silently reorder the history.
//     The backend has already done that walk, so {@link chainVersions} returns `versions` EXACTLY as
//     delivered and this module contains no comparator at all.
//
//   * A VERSION THE READER MAY NOT SEE IS ABSENT, AND SO IS THE POINTER NAMING IT. Confidentiality is
//     applied in SQL, so a closed-session version simply is not in the chain, and a pointer the reader
//     cannot follow is rendered absent rather than as an id that answers 404. Two consequences this
//     module is built around:
//
//       – NOTHING IS NUMBERED. "Version 3" would be a lie to a reader for whom the first two are
//         withheld; they would see the third as their first. Versions are identified by what they say
//         and when they were adopted, and by their position in the chain as shown.
//       – AN ABSENT PREDECESSOR RENDERS AS SILENCE. `supersedes_record_id` missing means either "this
//         is the earliest recorded version" or "the version it replaced is not yours to see", and this
//         module cannot tell them apart — so it says neither. It never fills the gap, and it never
//         reports one.
//
// `version_count` and `version_in_force` are BOTH computed over the scoped projection (the backend
// takes `len(projected)` and `projected[-1]`), so they describe what this reader received rather than
// what exists. That is what makes them safe to render as figures.

import { extractList } from '@givmo/api-client/list';
import type {
  OrgBoardGoverningDocumentOut,
  BoardGoverningDocumentTemplateOut,
  OrgBoardGoverningDocumentVersionOut,
} from '@givmo/api-client';
import { displayDate, humanize } from '../../lib/display';

export { displayDate, displayInstant, todayAsDateInput } from '../../lib/display';

// ── The instruments ──────────────────────────────────────────────────────────────────────────────

/**
 * The instrument kinds the backend's `BoardGoverningDocumentKind` enum ships, in its order.
 *
 * A CLOSED set duplicated from the wire enum on purpose: it drives the kind picker, and a picker built
 * from whatever kinds the organization happens to have recorded could never offer the first version of an
 * instrument they have not started. The registry itself has no entry for an unrecorded instrument —
 * deliberately, since a list of what an organization is missing is the plan's Band-3 line — so the picker is
 * the only place the full set appears, and it appears as a choice rather than as a checklist.
 */
export const DOCUMENT_KINDS: readonly { value: string; label: string }[] = [
  { value: 'bylaws', label: 'Bylaws' },
  { value: 'coi_policy', label: 'Conflict-of-interest policy' },
  { value: 'whistleblower_policy', label: 'Whistleblower policy' },
  { value: 'document_retention_policy', label: 'Document-retention policy' },
  { value: 'committee_charter', label: 'Committee charter' },
  { value: 'other', label: 'Another instrument' },
];

/** The kinds an organization holds several of, so each one needs a name of its own. */
const LABELLED_KINDS: readonly string[] = ['committee_charter', 'other'];

/**
 * Whether this kind REQUIRES a label — the backend refuses one without it, and refuses one on any
 * other kind. An organization holds several committee charters but one set of bylaws, and an optional label
 * would open two chains for one instrument.
 */
export function requiresLabel(kind: string | null | undefined): boolean {
  return LABELLED_KINDS.includes((kind ?? '').toLowerCase());
}

/** Human label for an instrument kind. */
export function documentKindLabel(kind: string | null | undefined): string {
  const match = DOCUMENT_KINDS.find((entry) => entry.value === (kind ?? '').toLowerCase());
  return match?.label ?? humanize(kind);
}

/** How one document reads as a heading — the instrument, plus which one for the labelled kinds. */
export function documentTitle(document: OrgBoardGoverningDocumentOut): string {
  const kind = documentKindLabel(document.document_kind);
  const label = (document.label ?? '').trim();
  return label.length > 0 ? `${kind} — ${label}` : kind;
}

/**
 * The registry's documents in a stable reading order: by instrument in the enum's order, then by
 * label. The backend returns them grouped but makes no ordering promise, and a registry whose cards
 * moved between visits would be read as records changing.
 */
export function sortDocuments(
  documents: readonly OrgBoardGoverningDocumentOut[],
): OrgBoardGoverningDocumentOut[] {
  const rank = (kind: string | null | undefined): number => {
    const index = DOCUMENT_KINDS.findIndex((entry) => entry.value === (kind ?? '').toLowerCase());
    return index === -1 ? DOCUMENT_KINDS.length : index;
  };
  return [...documents].sort((left, right) => {
    const byKind = rank(left.document_kind) - rank(right.document_kind);
    if (byKind !== 0) return byKind;
    return (left.label ?? '').localeCompare(right.label ?? '');
  });
}

/** The registry read. A bare array on the wire; tolerant of the envelope shape regardless. */
export function extractGoverningDocuments(body: unknown): OrgBoardGoverningDocumentOut[] {
  return sortDocuments(extractList<OrgBoardGoverningDocumentOut>(body));
}

// ── The chain ────────────────────────────────────────────────────────────────────────────────────

/**
 * A document's versions, in LINEAGE order, exactly as the backend walked them.
 *
 * Deliberately not sorted here. `order_chain` walks `supersedes_record_id` pointers and survives the
 * three shapes it can actually meet (an orphan whose predecessor the organization deleted, a fork
 * reachable only while one side is a draft, and a cycle unreachable through the API) by returning
 * every member rather than dropping any. Re-sorting by adoption date would undo that and would let a
 * back-dated version reorder a board's history.
 */
export function chainVersions(
  document: OrgBoardGoverningDocumentOut,
): OrgBoardGoverningDocumentVersionOut[] {
  return [...(document.versions ?? [])];
}

/** The version the backend marks as standing, or null when this reader can see none. */
export function versionInForce(
  document: OrgBoardGoverningDocumentOut,
): OrgBoardGoverningDocumentVersionOut | null {
  return document.version_in_force ?? null;
}

/**
 * Whether the backend marked THIS version as the one in force.
 *
 * Selected POSITIVELY (`=== true`) rather than by negation, the record core's rule: a negative test
 * admits any value a later build adds, in the permissive direction — and here the permissive direction
 * is telling an organization a superseded instrument is the one that governs them.
 */
export function isInForce(version: OrgBoardGoverningDocumentVersionOut): boolean {
  return version.is_in_force === true;
}

/** How the version's own adoption date reads. It IS the record's `occurred_on`, never a second field. */
export function adoptedOnLine(version: OrgBoardGoverningDocumentVersionOut): string {
  const adopted = version.adopted_on ?? version.record?.occurred_on ?? null;
  return adopted === null ? 'No adoption date recorded' : `Adopted ${displayDate(adopted)}`;
}

/** What a version's lineage pointer says, when it says anything. */
export interface VersionLineage {
  /** The sentence to render, in VERSION words — never correction words. */
  line: string;
  /** The predecessor's own record id, when the reader may follow the pointer AND it is in this chain. */
  predecessorRecordId: string | null;
  /** Its position in the chain as shown (0-based), when it is a member the reader received. */
  predecessorIndex: number | null;
}

/**
 * How one version's place in the chain reads — or `null`, which is the common and correct answer.
 *
 * Three shapes, and the third is the one worth getting right:
 *
 *   1. The pointer names a version in this chain → say which one, by what it is called.
 *   2. The pointer is present but names nothing shown → the reader is entitled to the id (they were
 *      given it), but the version itself is not in their chain. Say that it replaces an earlier
 *      version and stop; do not imply the earlier one is missing in error.
 *   3. The pointer is ABSENT → return null and render nothing. It means "this is the earliest version
 *      recorded" OR "the version it replaced was withheld from you", and nothing here can tell those
 *      apart. Saying "the earliest version" would be a claim, and saying "restricted" would disclose
 *      the existence of what the record store withheld. Absence is the honest render of absence.
 */
export function versionLineage(
  versions: readonly OrgBoardGoverningDocumentVersionOut[],
  version: OrgBoardGoverningDocumentVersionOut,
): VersionLineage | null {
  const predecessorId = version.supersedes_record_id ?? null;
  if (predecessorId === null) return null;

  const index = versions.findIndex((entry) => entry.record_id === predecessorId);
  if (index === -1) {
    return {
      line: 'Recorded as replacing an earlier version of this document.',
      predecessorRecordId: predecessorId,
      predecessorIndex: null,
    };
  }
  const predecessor = versions[index];
  const title = (predecessor?.record?.title ?? '').trim();
  return {
    line:
      title.length > 0
        ? `Recorded as replacing “${title}”.`
        : 'Recorded as replacing the version before it.',
    predecessorRecordId: predecessorId,
    predecessorIndex: index,
  };
}

/**
 * The record of the act that adopted this version, when the reader may follow that pointer.
 *
 * `adopted_by_record_id` is scoped exactly like every other record-to-record pointer, so a policy
 * adopted in a closed session renders with no adopting act for a reader who was not in that room —
 * absent, not "restricted".
 */
export function adoptingRecordId(version: OrgBoardGoverningDocumentVersionOut): string | null {
  return version.adopted_by_record_id ?? null;
}

/**
 * The storage reference for an attachment the MODULE holds against this version.
 *
 * ⚠ REF-STRINGS ONLY, and only for a module-authored version. `artifact_ref` and
 * `imported_artifact_ref` are disjoint by CHECK — a module record's attachment lands in the first, an
 * imported record's pre-module original in the second — and the ordinary record projection carries
 * only the first. So an IMPORTED version's attachment reference is not on this read at all, and the
 * screen must not imply it is missing: it renders the reference it was given and says nothing about
 * one it was not. There is no upload here either way; the bytes live in the organization's own storage and
 * this is the pointer they already use.
 */
export function artifactRef(version: OrgBoardGoverningDocumentVersionOut): string | null {
  const ref = (version.artifact_ref ?? version.record?.artifact_ref ?? '').trim();
  return ref.length > 0 ? ref : null;
}

/** Whether the organization entered this version as history rather than authoring it here. */
export function isImportedVersion(version: OrgBoardGoverningDocumentVersionOut): boolean {
  return version.record?.provenance === 'imported';
}

/** How a version's provenance reads. Positive on both arms so an unknown value lands in neither. */
export function provenanceLine(version: OrgBoardGoverningDocumentVersionOut): string | null {
  const provenance = version.record?.provenance ?? null;
  if (provenance === 'imported') {
    const note = (version.record?.imported_source_note ?? '').trim();
    return note.length > 0
      ? `Imported from your earlier records — ${note}`
      : 'Imported from your earlier records.';
  }
  if (provenance === 'module') return null;
  return null;
}

// ── The form library ─────────────────────────────────────────────────────────────────────────────

/** The library read (list projection — bodies withheld until a form is opened by id). */
export function extractGoverningDocumentTemplates(
  body: unknown,
): BoardGoverningDocumentTemplateOut[] {
  return extractList<BoardGoverningDocumentTemplateOut>(body);
}

/**
 * The forms the library ships for one instrument.
 *
 * ⚠ AN EMPTY RESULT IS AN ORDINARY ANSWER, AND THERE IS NO BYLAWS FORM BY DESIGN. Bylaws allocate the
 * organization's own powers, and a form filled in from a menu is the plan's Band-3 line, so the
 * library ships none — an organization's bylaws arrive through the version chain instead. The UI therefore
 * renders no forms section at all when this is empty rather than explaining the absence: an
 * explanation would be product voice about what an organization's instruments ought to look like, which is
 * the thing the whole rail exists to prevent.
 */
export function templatesForKind(
  templates: readonly BoardGoverningDocumentTemplateOut[],
  kind: string | null | undefined,
): BoardGoverningDocumentTemplateOut[] {
  const wanted = (kind ?? '').toLowerCase();
  return templates.filter((template) => (template.document_kind ?? '').toLowerCase() === wanted);
}
